var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import client from 'api/environment';
import ADD_TENANT_SEASON from 'api/mutations/ADD_TENANT_SEASON';
import REMOVE_TENANT_SEASON from 'api/mutations/REMOVE_TENANT_SEASON';
import SET_WORKING_SEASON from 'api/mutations/SET_WORKING_SEASON';
import UPDATE_TENANT_SEASON from 'api/mutations/UPDATE_TENANT_SEASON';
import GET_CURRENT_TENANT_SEASONS from 'api/queries/GET_CURRENT_TENANT_SEASONS';
export const initialState = {
    items: [],
    pageInfo: {
        hasNextPage: true,
        endCursor: null,
    },
    status: {
        fetch: 'idle',
        add: 'idle',
        update: 'idle',
        remove: 'idle',
        setWorking: 'idle',
    },
    errors: {
        fetch: null,
        add: null,
        update: null,
        remove: null,
        setWorking: null,
    },
    refetchFlags: {
        fetch: false,
        add: false,
        update: false,
        remove: false,
    },
};
export const fetchSeasons = createAsyncThunk('seasons/fetchSeasons', (_, { getState, rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    const state = getState();
    const { pageInfo } = state.parameters.seasons;
    try {
        const response = yield client.query({
            query: GET_CURRENT_TENANT_SEASONS,
            variables: {
                first: 25,
                after: pageInfo.endCursor,
            },
            fetchPolicy: 'network-only',
        });
        return response.data.me.currentTenant.seasons;
    }
    catch (error) {
        const apolloError = error;
        return rejectWithValue(apolloError.message);
    }
}));
export const addSeason = createAsyncThunk('seasons/addSeason', ({ tenantId, name }) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const seasonData = yield client.mutate({
            mutation: ADD_TENANT_SEASON,
            variables: { tenantId, name },
            fetchPolicy: 'no-cache',
        });
        return seasonData.data.addSeason.season;
    }
    catch (error) {
        const apolloError = error;
        return apolloError.message;
    }
}));
export const updateSeason = createAsyncThunk('seasons/updateSeason', ({ seasonId, name }) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const seasonData = yield client.mutate({
            mutation: UPDATE_TENANT_SEASON,
            variables: { seasonId, name },
        });
        return seasonData.data.updateSeason.season;
    }
    catch (error) {
        const apolloError = error;
        return apolloError.message;
    }
}));
export const removeSeason = createAsyncThunk('seasons/removeSeason', (seasonId) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        yield client.mutate({
            mutation: REMOVE_TENANT_SEASON,
            variables: { seasonId },
        });
        return seasonId;
    }
    catch (error) {
        const apolloError = error;
        return apolloError.message;
    }
}));
export const setWorkingSeason = createAsyncThunk('seasons/setWorkingSeason', (seasonId, { rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const seasonData = yield client.mutate({
            mutation: SET_WORKING_SEASON,
            variables: { seasonId },
        });
        return seasonData.data.setWorkingSeason.season;
    }
    catch (error) {
        const apolloError = error;
        return rejectWithValue(apolloError.message);
    }
}));
const seasonsSlice = createSlice({
    name: 'seasons',
    initialState,
    reducers: {
        resetSeasons(state) {
            state.items = [];
            state.pageInfo = initialState.pageInfo;
            state.status = initialState.status;
            state.errors = initialState.errors;
            state.refetchFlags = initialState.refetchFlags;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchSeasons.pending, (state) => {
            state.status.fetch = 'loading';
            state.refetchFlags.fetch = false;
        })
            .addCase(fetchSeasons.fulfilled, (state, action) => {
            var _a;
            const newSeasons = action.payload.edges.map((edge) => edge.node);
            const uniqueSeasons = newSeasons.filter((newSeason) => !state.items.some((existingSeason) => existingSeason.id === newSeason.id));
            state.items = [...state.items, ...uniqueSeasons];
            state.pageInfo = (_a = action.payload) === null || _a === void 0 ? void 0 : _a.pageInfo;
            state.status.fetch = 'succeeded';
        })
            .addCase(fetchSeasons.rejected, (state, action) => {
            state.status.fetch = 'failed';
            state.errors.fetch = action.error.message || 'Error fetching components';
        })
            .addCase(addSeason.pending, (state) => {
            state.status.add = 'loading';
        })
            .addCase(addSeason.fulfilled, (state, action) => {
            var _a, _b, _c, _d, _e, _f;
            state.items = [
                ...state.items,
                Object.assign(Object.assign({}, action.payload), { canNameBeUpdated: (_b = (_a = action.payload) === null || _a === void 0 ? void 0 : _a.canNameBeUpdated) !== null && _b !== void 0 ? _b : true, canBeRemoved: (_d = (_c = action.payload) === null || _c === void 0 ? void 0 : _c.canBeRemoved) !== null && _d !== void 0 ? _d : true, workingSeason: (_f = (_e = action.payload) === null || _e === void 0 ? void 0 : _e.workingSeason) !== null && _f !== void 0 ? _f : false }),
            ];
            state.status.add = 'succeeded';
        })
            .addCase(addSeason.rejected, (state, action) => {
            state.status.add = 'failed';
            state.errors.add = action.error.message || 'Failed to add specification';
        })
            .addCase(updateSeason.pending, (state) => {
            state.status.update = 'loading';
        })
            .addCase(updateSeason.fulfilled, (state, action) => {
            const { id, name, workingSeason, canNameBeUpdated, canBeRemoved } = action.payload;
            const itemIndex = state.items.findIndex((item) => item.id === id);
            if (itemIndex !== -1) {
                state.items[itemIndex] = Object.assign(Object.assign({}, state.items[itemIndex]), { name, workingSeason: typeof workingSeason === 'boolean'
                        ? workingSeason
                        : state.items[itemIndex].workingSeason, canNameBeUpdated: typeof canNameBeUpdated === 'boolean'
                        ? canNameBeUpdated
                        : state.items[itemIndex].canNameBeUpdated, canBeRemoved: typeof canBeRemoved === 'boolean'
                        ? canBeRemoved
                        : state.items[itemIndex].canBeRemoved });
            }
            state.status.update = 'succeeded';
        })
            .addCase(updateSeason.rejected, (state, action) => {
            state.status.update = 'failed';
            state.errors.update = action.error.message || 'Failed to add specification';
        })
            .addCase(removeSeason.pending, (state) => {
            state.status.remove = 'loading';
        })
            .addCase(removeSeason.fulfilled, (state, action) => {
            state.items = state.items.filter((order) => order.id !== action.payload);
            state.status.remove = 'succeeded';
        })
            .addCase(removeSeason.rejected, (state, action) => {
            state.status.remove = 'failed';
            state.errors.remove = action.payload;
        })
            .addCase(setWorkingSeason.pending, (state) => {
            state.status.setWorking = 'loading';
            state.errors.setWorking = null;
        })
            .addCase(setWorkingSeason.fulfilled, (state, action) => {
            const updatedSeason = action.payload;
            let matchedSeason = false;
            state.items = state.items.map((season) => {
                if (season.id === updatedSeason.id) {
                    matchedSeason = true;
                    return Object.assign(Object.assign(Object.assign({}, season), updatedSeason), { workingSeason: true });
                }
                return Object.assign(Object.assign({}, season), { workingSeason: false });
            });
            if (!matchedSeason) {
                state.items = [
                    ...state.items,
                    Object.assign(Object.assign({}, updatedSeason), { workingSeason: true }),
                ];
            }
            state.status.setWorking = 'succeeded';
            state.errors.setWorking = null;
        })
            .addCase(setWorkingSeason.rejected, (state, action) => {
            state.status.setWorking = 'failed';
            state.errors.setWorking =
                action.payload || action.error.message || 'Failed to set working season';
        });
    },
});
export const { resetSeasons } = seasonsSlice.actions;
export const selectSeasons = (state) => state.parameters.seasons.items;
export const selectSeasonById = (state, seasonId) => state.parameters.seasons.items.find((season) => season.id === seasonId);
export const selectSeasonsPageInfo = (state) => state.parameters.seasons.pageInfo;
export const selectSeasonsStatus = (state) => state.parameters.seasons.status;
export const selectSeasonsErrors = (state) => state.parameters.seasons.errors;
export const selectSeasonsRefetchFlags = (state) => state.parameters.seasons.refetchFlags;
export default seasonsSlice.reducer;
